import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
// Bundle the pdf.js worker locally (Vite ?url asset) — a CDN worker can be
// blocked by CSP/CORS and silently fail to render the preview.
// @ts-ignore — Vite resolves the ?url suffix to the built asset URL at build time.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { Sidebar } from '../../components/Sidebar';
import { Header } from '../../components/header';
import '../../scss/create-contract.scss';
import { fetchCustomers, Customer } from '../../api/customer';
import {
    createCustomerOfferApi,
    getCustomerOfferByIdApi,
    getAllCustomerOffersApi,
    getPreferredOfferLanguageApi,
    previewOfferPdfApi,
    sendOfferAgreementEmail,
    OFFER_STATUS_ACCEPTED,
} from '../../features/customer-offer/customer-offer.service';
import { fetchPersonalData } from '../../features/profile-settings/profile-settings.slice';
import { formatOfferDate } from '../../features/customer-offer/customer-offer-date.util';
import { resolveOfferLang, OFFER_LANG_STORAGE_KEY } from '../../features/customer-offer/customer-offer-language.util';
import {
    CheckIcon, StarsIcon,
    ImageIcon, PeopleIcon, WalletIcon, ScaleIcon,
} from './create-contract/icons';
import {
    Step0Props, Step1Props, Step2Props, Step3Props, StepBaseProps,
} from './create-offer/types';
import Step0LogoProfile             from './create-offer/step0-logo-profile.component';
import Step1Parties                 from './create-offer/step1-parties.component';
import Step2AssignmentCompensation  from './create-offer/step2-assignment-compensation.component';
import Step3LegalSignatures         from './create-offer/step3-legal-signatures.component';

// pdf.js worker — served from our own bundle, version-matched to react-pdf's pdfjs.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Render the title with the LAST word styled as a violet gradient brand
// accent — works for any locale. Single-word titles are fully styled.
const renderHeroTitle = (title: string): React.ReactNode => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const idx = trimmed.lastIndexOf(' ');
    if (idx === -1) return <span className="cc-hero__brand">{trimmed}</span>;
    return (
        <>
            {trimmed.slice(0, idx)}{' '}
            <span className="cc-hero__brand">{trimmed.slice(idx + 1)}</span>
        </>
    );
};

// First-mount shimmer skeleton that mirrors the stepper + first card layout.
const CoSkeleton: React.FC = () => (
    <>
        <div className="cc-skel cc-skel-stepper" />
        <div className="cc-skel-card">
            <div className="cc-skel-line cc-skel-line--title" />
            <div className="cc-skel-line" />
            <div className="cc-skel-line" />
            <div className="cc-skel-line cc-skel-line--short" />
        </div>
    </>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const CreateOfferPage: React.FC = () => {
    const navigate     = useNavigate();
    const [lang, setLang] = useState(resolveOfferLang);
    const language = new URLSearchParams(window.location.search).get('lang')
    const offerId      = new URLSearchParams(window.location.search).get('id');
    const duplicateId  = new URLSearchParams(window.location.search).get('duplicateId');
    const sourceId     = offerId || duplicateId;
    const isEditMode   = !!offerId;
    const isDuplicateMode = !offerId && !!duplicateId;
    const { t }        = useTranslation();
    const dispatch     = useDispatch<any>();
    const userId       = useSelector((state: any) => state.auth?.user?.userId);
    const personalData = useSelector((state: any) => state.profileSettings?.personalData);

    const steps = [
        { label: t('CO_STEP_COMPANY_LOGO'),             subtitle: t('CO_STEP_COMPANY_LOGO_SUB'),             icon: <ImageIcon /> },
        { label: t('CO_STEP_PARTIES_INFO'),             subtitle: t('CO_STEP_PARTIES_INFO_SUB'),             icon: <PeopleIcon /> },
        { label: t('CO_STEP_ASSIGNMENT_COMPENSATION'),  subtitle: t('CO_STEP_ASSIGNMENT_COMPENSATION_SUB'),  icon: <WalletIcon /> },
        { label: t('CO_STEP_LEGAL_SIGNATURES'),         subtitle: t('CO_STEP_LEGAL_SIGNATURES_SUB'),         icon: <ScaleIcon /> },
    ];

    // ── Shared UI state ───────────────────────────────────────────────────────

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentStep,   setCurrentStep]   = useState(0);
    const [isSubmitting,  setIsSubmitting]   = useState(false);
    const [submitAction,  setSubmitAction]   = useState<'draft' | 'send' | null>(null);
    const [errors,        setErrors]         = useState<Record<string, string>>({});
    const [isLoadingOffer, setIsLoadingOffer] = useState(!!sourceId);
    const [booting,           setBooting]           = useState(true);

    // Nothing is saved yet at preview time — the PDF is rendered from the
    // current in-memory form state via the preview-pdf endpoint. create +
    // send-email only happen once the sender confirms inside the modal.
    interface OfferPreview {
        pdfUrl: string;
        senderName: string;
    }
    const [previewOffer, setPreviewOffer] = useState<OfferPreview | null>(null);
    const [isSendingFromPreview, setIsSendingFromPreview] = useState(false);

    // ── PDF preview rendering (react-pdf) + custom swipe navigation ────────────
    const [previewNumPages, setPreviewNumPages] = useState(0);
    const [previewPageWidth, setPreviewPageWidth] = useState(0);
    const [previewAtTop,     setPreviewAtTop]     = useState(true);
    const [previewAtBottom,  setPreviewAtBottom]  = useState(false);
    const previewScrollRef = useRef<HTMLDivElement>(null);

    const syncPreviewEdges = () => {
        const el = previewScrollRef.current;
        if (!el) return;
        setPreviewAtTop(el.scrollTop <= 2);
        setPreviewAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
    };

    // Smooth "swipe" scroll — dir 1 = down, -1 = up (~85% of a viewport).
    const swipePreview = (dir: 1 | -1) => {
        const el = previewScrollRef.current;
        if (!el) return;
        el.scrollBy({ top: dir * el.clientHeight * 0.85, behavior: 'smooth' });
    };

    // Fit each page to the frame width; reset scroll edges when the modal opens.
    useEffect(() => {
        if (!previewOffer) return;
        const measure = () => {
            const el = previewScrollRef.current;   // the scrollable modal body
            if (el) setPreviewPageWidth(Math.max(0, el.clientWidth - 44));
        };
        measure();
        setPreviewNumPages(0);
        setPreviewAtTop(true);
        setPreviewAtBottom(false);
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [previewOffer]);

    // Re-check edges once pages have laid out (content height is now known).
    useEffect(() => { syncPreviewEdges(); }, [previewNumPages, previewPageWidth]);

    // ── Step 0 ────────────────────────────────────────────────────────────────
    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    // ── Step 1 (Parties) ─────────────────────────────────────────────────────
    const [customers,            setCustomers]            = useState<Customer[]>([]);
    const [selectedCustomerId,   setSelectedCustomerId]   = useState<number | null>(null);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [contactPerson,        setContactPerson]        = useState('');
    const [contactPhone,         setContactPhone]         = useState('');
    const [offeredServices,      setOfferedServices]      = useState('');
    const [workDescription,      setWorkDescription]      = useState('');

    // ── Step 2 (Assignment & Compensation) ────────────────────────────────────
    const [assignmentPeriod, setAssignmentPeriod] = useState('');
    const [startDate,       setStartDate]       = useState('');
    const [endDate,         setEndDate]         = useState('');
    const [noEndDate,       setNoEndDate]       = useState(false);
    const [hasNoticePeriod, setHasNoticePeriod] = useState(false);
    const [noticePeriod,    setNoticePeriod]    = useState('');
    const [assignmentDesc,  setAssignmentDesc]  = useState('');
    const [specialTerms,    setSpecialTerms]    = useState('');
    const [compType,        setCompType]        = useState('');
    const [unit,            setUnit]            = useState('');
    const [currency,        setCurrency]        = useState('SEK');
    const [amount,          setAmount]          = useState('');

    // ── Step 3 (Legal & Signatures) ───────────────────────────────────────────
    const [hasAgreed,    setHasAgreed]    = useState(false);
    // Always today's date — not user-editable, so it isn't loaded from a
    // saved offer or exposed as an input the user can change.
    const [signDate]                      = useState(() => formatOfferDate(new Date().toISOString()));
    const [signerName,   setSignerName]   = useState(personalData?.fullName || '');
    const [hasSignature, setHasSignature] = useState(false);
    const [existingSignatureData, setExistingSignatureData] = useState<string | undefined>(undefined);
    // Status of the offer being edited (null for a fresh create/duplicate)
    // — an already-Accepted offer can't be sent again.
    const [loadedOfferStatus, setLoadedOfferStatus] = useState<number | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // ── First-mount skeleton (~1s) ──────────────────────────────────────────────
    useEffect(() => {
        const id = setTimeout(() => setBooting(false), 1000);
        return () => clearTimeout(id);
    }, []);

    // Scoped to this offer only — does not touch the platform-wide UI
    // language (that's the Header's own dropdown).
    const handleOfferLangChange = (newLang: string) => {
        setLang(newLang);
        window.localStorage.setItem(OFFER_LANG_STORAGE_KEY, newLang);
    };
    useEffect(() => {
        if (window.localStorage.getItem(OFFER_LANG_STORAGE_KEY)) return;

        getPreferredOfferLanguageApi(lang)
            .then(res => {
                const preferred = res.response;
                if (preferred && preferred !== lang) handleOfferLangChange(preferred);
            })
            .catch(() => { /* keep the locally-resolved default */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prefill the signer's name clarification from the profile once it loads
    // (fresh create only; never overwrites a loaded offer or user edits).
    useEffect(() => {
        if (offerId) return; // edit mode loads the saved name instead
        const profileName =
            personalData?.fullName?.trim() ||
            [personalData?.firstName, personalData?.lastName].filter(Boolean).join(' ').trim();
        if (profileName) setSignerName((prev: string) => (prev.trim() ? prev : profileName));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personalData, offerId]);

    // ── Data loading ──────────────────────────────────────────────────────────

    useEffect(() => {
        const loadLogo = async () => {
            if (!userId) return;
            try {
                const userData = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
                const token    = userData.token || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
                const baseUrl  = (import.meta as any).env.VITE_API_URL || '';
                const res      = await fetch(`${baseUrl}/signup/invoice-logo-stream?userId=${userId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const blob = await res.blob();
                    if (blob.size > 0) setLogoUrl(URL.createObjectURL(blob));
                }
            } catch { /* no logo */ }
        };

        const loadCustomers = async () => {
            try {
                const res = await fetchCustomers({ pageSize: 100 });
                setCustomers(res.response || []);
            } catch {
                toast.error(t('CO_TOAST_ERROR') as string);
            }
        };

        loadLogo();
        loadCustomers();
        dispatch(fetchPersonalData(userId));
    }, [userId]);

    useEffect(() => {
        if (personalData?.firstName) setSignerName(personalData.firstName);
    }, [personalData?.firstName]);

    // ── Load existing offer for edit ─────────────────────────────────────────

    useEffect(() => {
        if (!sourceId) return;

        const loadOffer = async () => {
            try {
                const res = await getCustomerOfferByIdApi(Number(sourceId));
                const o = res.response;

                setSelectedCustomerId(o.customer?.customerId ?? null);
                setContactPerson(o.contactPerson || '');
                setContactPhone(o.contactPhone || '');
                setOfferedServices(o.offeredServices || '');
                setWorkDescription(o.workDescription || '');

                setAssignmentPeriod(o.assignmentPeriod || '');
                setSpecialTerms(o.specialTerms || '');
                setHasNoticePeriod(o.hasNoticePeriod);
                setNoticePeriod(o.noticePeriodDuration || '');
                setAssignmentDesc(o.assignmentDescription || '');

                setCompType(o.compensationType || '');
                setUnit(o.numberOfUnits != null ? String(o.numberOfUnits) : '');
                setCurrency(o.currency || 'SEK');
                setAmount(o.amount != null ? String(o.amount) : '');

                if (isDuplicateMode) {
                    setStartDate('');
                    setEndDate('');
                    setNoEndDate(false);
                } else {
                    setStartDate(o.startDate ? o.startDate.split('T')[0] : '');
                    setEndDate(o.endDate ? o.endDate.split('T')[0] : '');
                    setNoEndDate(o.noEndDate);
                    setLoadedOfferStatus(o.offerStatus);

                    setHasAgreed(o.isTermsAccepted);
                    if (o.freelancerNameClarification) setSignerName(o.freelancerNameClarification);
                    if (o.freelancerSignatureData) setExistingSignatureData(o.freelancerSignatureData);
                }
            } catch {
                toast.error(t('CO_TOAST_LOAD_ERROR') as string);
                navigate(`/v2/my-contracts?lang=${language}&tab=offers`);
            } finally {
                setIsLoadingOffer(false);
            }
        };

        loadOffer();
    }, [sourceId]);

    // ── Validation ────────────────────────────────────────────────────────────

    const validateStep = (step: number): Record<string, string> => {
        const e: Record<string, string> = {};

        if (step === 1) {
            if (!selectedCustomerId)     e.customer        = t('CO_VAL_CUSTOMER_REQUIRED');
            if (!contactPerson.trim())   e.contactPerson   = t('CO_VAL_FIELD_REQUIRED');
            if (!contactPhone.trim())    e.contactPhone    = t('CO_VAL_FIELD_REQUIRED');
            if (!offeredServices.trim()) e.offeredServices = t('CO_VAL_FIELD_REQUIRED');
            if (!workDescription.trim()) e.workDescription = t('CO_VAL_FIELD_REQUIRED');
        }

        if (step === 2) {
            if (!startDate) e.startDate = t('CO_VAL_FIELD_REQUIRED');
            if (!noEndDate && !endDate) {
                e.endDate = t('CO_VAL_END_DATE_REQUIRED');
            } else if (startDate && endDate && !noEndDate && new Date(endDate) <= new Date(startDate)) {
                e.endDate = t('CO_VAL_END_AFTER_START');
            }
            if (!assignmentDesc.trim())                e.assignmentDesc = t('CO_VAL_FIELD_REQUIRED');
            if (!compType)                              e.compType       = t('CO_VAL_FIELD_REQUIRED');
            if (compType === 'per_unit' && !unit.trim()) e.unit          = t('CO_VAL_FIELD_REQUIRED');
            if (!amount || parseFloat(amount) <= 0)     e.amount         = t('CO_VAL_AMOUNT_POSITIVE');
        }

        if (step === 3) {
            if (!hasSignature)      e.signature  = t('CO_VAL_SIGNATURE_REQUIRED');
            if (!signerName.trim()) e.signerName = t('CO_VAL_FIELD_REQUIRED');
            if (!hasAgreed)         e.hasAgreed  = t('CO_VAL_TERMS_REQUIRED');
        }

        return e;
    };

    // Maps each validation error key → an existing (already-localised) UI label
    // key, so the error toast can name the unfilled field(s) in any language.
    const FIELD_LABEL_KEYS: Record<string, string> = {
        customer:        'COG_COL_CUSTOMER',
        contactPerson:   'CO_CONTACT_PERSON_LABEL',
        contactPhone:    'CO_CONTACT_PHONE_LABEL',
        offeredServices: 'CO_OFFERED_SERVICES_LABEL',
        workDescription: 'CO_WORK_DESC_LABEL',
        startDate:       'CO_ASSIGN_START_DATE',
        endDate:         'CO_ASSIGN_END_DATE',
        assignmentDesc:  'CO_ASSIGN_DESC_TITLE',
        compType:        'CO_COMP_TYPE_LABEL',
        unit:            'CO_COMP_UNIT_LABEL',
        amount:          'CO_COMP_AMOUNT_LABEL',
        signature:       'CO_SIG_SIGNATURE_LABEL',
        signerName:      'CO_SIG_NAME_LABEL',
        hasAgreed:       'CO_LEGAL_CARD_TITLE',
    };

    // Toast the missing field names (localised). Falls back to a generic prompt.
    const showValidationToast = (e: Record<string, string>) => {
        const labels = Object.keys(e)
            .map(k => FIELD_LABEL_KEYS[k])
            .filter(Boolean)
            .map(key => String(t(key)).replace(/[:：]\s*$/, '').trim());
        toast.error(
            labels.length
                ? `${t('CO_TOAST_FILL_REQUIRED')}: ${labels.join(', ')}`
                : (t('CO_TOAST_FILL_REQUIRED') as string)
        );
    };

    // After a failed validation, bring the first invalid field into view so the
    // user sees exactly what's missing (runs after the error classes render).
    const scrollToFirstError = () => {
        requestAnimationFrame(() => {
            const el = document.querySelector(
                '.cc-step-content .cc-input--error, .cc-step-content .cc-canvas-wrap--error, .cc-step-content .cc-confirm-row--error, .cc-step-content .cc-field-error'
            ) as HTMLElement | null;
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (el.matches('input, textarea, select')) {
                (el as HTMLInputElement).focus({ preventScroll: true });
            }
        });
    };

    // ── Navigation ────────────────────────────────────────────────────────────

    const goNext = () => {
        const e = validateStep(currentStep);
        if (Object.keys(e).length > 0) { setErrors(e); showValidationToast(e); scrollToFirstError(); return; }
        setErrors({});
        setCurrentStep(s => Math.min(s + 1, steps.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goBack = () => {
        setErrors({});
        setCurrentStep(s => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // The create/update endpoint doesn't return the saved offer's id, so
    // when sending a brand-new (or duplicated) offer we look it up by
    // customer right after saving, picking the most recently created match.
    const findJustSavedOfferId = async (customerId: number): Promise<number | null> => {
        try {
            const res = await getAllCustomerOffersApi();
            const matches = (res.response || []).filter(o => o.customer?.customerId === customerId);
            if (matches.length === 0) return null;
            matches.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
            return matches[0].id;
        } catch {
            return null;
        }
    };

    // Shared field set for both the create payload and the preview-pdf
    // payload — always reads whatever is currently on the canvas/form.
    const buildCreatePayload = (id?: number) => {
        const signatureData = (canvasRef.current?.toDataURL('image/png') || '').split(',')[1] || '';
        return {
            ...(id ? { id } : {}),
            customerId:                selectedCustomerId!,
            language:                  lang,
            offeredServices,
            assignmentPeriod,
            workDescription,
            specialTerms,
            startDate:                 startDate ? new Date(startDate).toISOString() : '',
            endDate:                   endDate   ? new Date(endDate).toISOString()   : '',
            noEndDate,
            hasNoticePeriod,
            noticePeriodDuration:      noticePeriod,
            assignmentDescription:     assignmentDesc,
            compensationType:          compType,
            currency,
            amount:                    parseFloat(amount) || 0,
            numberOfUnits:             compType === 'per_unit' ? (parseFloat(unit) || 0) : 0,
            contactPerson,
            contactPhone,
            isTermsAccepted:           hasAgreed,
            isDraft:                   false,
            freelancerSignatureDate:     signDate ? new Date(signDate).toISOString() : '',
            freelancerSignatureData:     signatureData || existingSignatureData || '',
            freelancerNameClarification: signerName,
        };
    };

    const handleSave = async (action: 'draft' | 'send') => {
        if (action === 'send' && loadedOfferStatus === OFFER_STATUS_ACCEPTED) {
            toast.error(t('COG_ACTION_SEND_DISABLED_ACCEPTED') as string);
            return;
        }

        const e = validateStep(3);
        if (Object.keys(e).length > 0) { setErrors(e); showValidationToast(e); scrollToFirstError(); return; }
        if (!selectedCustomerId) { toast.error(t('CO_TOAST_CUSTOMER_REQUIRED') as string); return; }

        try {
            setIsSubmitting(true);
            setSubmitAction(action);

            if (action === 'draft') {
                await createCustomerOfferApi({
                    ...buildCreatePayload(isEditMode ? Number(offerId) : undefined),
                    isDraft: true,
                });
                toast.success(t(isEditMode ? 'CO_TOAST_UPDATE_SUCCESS' : 'CO_TOAST_SUCCESS') as string);
                navigate(`/v2/my-contracts?lang=${language}&tab=offers`);
                return;
            }

            // action === 'send' — preview only. Nothing is created/saved and
            // no email is sent until the sender confirms inside the modal.
            const customer = customers.find(c => c.customerId === selectedCustomerId);
            if (!customer?.email) {
                toast.error(t('COG_SEND_NO_EMAIL') as string);
                return;
            }

            const signatureData = (canvasRef.current?.toDataURL('image/png') || '').split(',')[1] || '';
            const pdfBlob = await previewOfferPdfApi({
                ...(isEditMode ? { id: Number(offerId) } : {}),
                customerId:                selectedCustomerId,
                language:                  lang,
                offeredServices,
                assignmentPeriod,
                workDescription,
                specialTerms,
                startDate:                 startDate ? new Date(startDate).toISOString() : '',
                endDate:                   endDate   ? new Date(endDate).toISOString()   : '',
                noEndDate,
                hasNoticePeriod,
                noticePeriodDuration:      noticePeriod,
                assignmentDescription:     assignmentDesc,
                compensationType:          compType,
                currency,
                amount:                    parseFloat(amount) || 0,
                numberOfUnits:             compType === 'per_unit' ? (parseFloat(unit) || 0) : 0,
                contactPerson,
                contactPhone,
                isTermsAccepted:           hasAgreed,
                customerSignatureDate:     '',
                customerSignatureData:     '',
                customerNameClarification: '',
                freelancerSignatureDate:     signDate ? new Date(signDate).toISOString() : '',
                freelancerSignatureData:     signatureData || existingSignatureData || '',
                freelancerNameClarification: signerName,
                isDraft: true,
            }, lang);

            const senderName =
                personalData?.fullName?.trim() ||
                [personalData?.firstName, personalData?.lastName].filter(Boolean).join(' ').trim();

            setPreviewOffer({ pdfUrl: URL.createObjectURL(pdfBlob), senderName });
        } catch {
            toast.error(t(
                action === 'send'
                    ? 'CO_TOAST_SEND_ERROR'
                    : (isEditMode ? 'CO_TOAST_UPDATE_ERROR' : 'CO_TOAST_ERROR')
            ) as string);
        } finally {
            setIsSubmitting(false);
            setSubmitAction(null);
        }
    };

    const closePreview = () => {
        if (previewOffer) URL.revokeObjectURL(previewOffer.pdfUrl);
        setPreviewOffer(null);
    };

    const handleConfirmSendFromPreview = async () => {
        if (!previewOffer || !selectedCustomerId) return;
        try {
            setIsSendingFromPreview(true);

            // "Send to Customer" is the point of no return — save the
            // offer for real, then email it.
            await createCustomerOfferApi({
                ...buildCreatePayload(isEditMode ? Number(offerId) : undefined),
                isDraft: false,
            });

            const targetId = isEditMode ? Number(offerId) : await findJustSavedOfferId(selectedCustomerId);
            if (!targetId) {
                toast.error(t('CO_TOAST_SEND_LOOKUP_ERROR') as string);
                closePreview();
                navigate(`/v2/my-contracts?lang=${language}&tab=offers`);
                return;
            }

            const detail = (await getCustomerOfferByIdApi(targetId)).response;
            if (!detail.customer?.email) {
                toast.error(t('COG_SEND_NO_EMAIL') as string);
                closePreview();
                navigate(`/v2/my-contracts?lang=${language}&tab=offers`);
                return;
            }

            await sendOfferAgreementEmail(
                targetId,
                {
                    customerName:     detail.customer.name,
                    customerEmail:    detail.customer.email,
                    offerNumber:      detail.offerNumber,
                    offeredServices:  detail.offeredServices,
                    startDate:        detail.startDate,
                    endDate:          detail.endDate,
                    noEndDate:        detail.noEndDate,
                    amount:           detail.amount,
                    currency:         detail.currency,
                    compensationType: detail.compensationType,
                },
                previewOffer.senderName,
                lang,
                detail.offerStatus
            );

            toast.success(t('CO_TOAST_SEND_SUCCESS') as string);
            closePreview();
            navigate(`/v2/my-contracts?lang=${language}&tab=offers`);
        } catch {
            toast.error(t('CO_TOAST_SEND_ERROR') as string);
        } finally {
            setIsSendingFromPreview(false);
        }
    };

    // ── renderStep ────────────────────────────────────────────────────────────

    const renderStep = (): React.ReactNode => {
        const shared: StepBaseProps = { t, errors, setErrors };

        switch (currentStep) {
            case 0: {
                const props: Step0Props = {
                    ...shared,
                    logoUrl,
                    personalData,
                    lang,
                    onLangChange: handleOfferLangChange,
                    onNavigateToProfile: () => navigate(`/profile-settings?tab=preferences&lang=${lang}`),
                };
                return <Step0LogoProfile {...props} />;
            }
            case 1: {
                const props: Step1Props = {
                    ...shared,
                    customers,
                    selectedCustomerId,
                    setSelectedCustomerId,
                    showCustomerDropdown,
                    setShowCustomerDropdown,
                    contactPerson,
                    setContactPerson,
                    contactPhone,
                    setContactPhone,
                    offeredServices,
                    setOfferedServices,
                    assignmentPeriod,
                    setAssignmentPeriod,
                    workDescription,
                    setWorkDescription,
                    specialTerms,
                    setSpecialTerms,
                    onCustomerAdded: async () => {
                        try {
                            const res = await fetchCustomers({ pageSize: 100 });
                            setCustomers(res.response || []);
                        } catch { /* keep existing list */ }
                    },
                };
                return <Step1Parties {...props} />;
            }
            case 2: {
                const props: Step2Props = {
                    ...shared,
                    startDate,   setStartDate,
                    endDate,     setEndDate,
                    noEndDate,   setNoEndDate,
                    hasNoticePeriod, setHasNoticePeriod,
                    noticePeriod,    setNoticePeriod,
                    assignmentDesc,  setAssignmentDesc,
                    compType,    setCompType,
                    unit,        setUnit,
                    currency,    setCurrency,
                    amount,      setAmount,
                };
                return <Step2AssignmentCompensation {...props} />;
            }
            case 3: {
                const props: Step3Props = {
                    ...shared,
                    hasAgreed,    setHasAgreed,
                    signDate,
                    signerName,   setSignerName,
                    hasSignature, setHasSignature,
                    canvasRef,
                    initialSignatureData: existingSignatureData,
                    customerName: customers.find(c => c.customerId === selectedCustomerId)?.name,
                    isCustomerSigned: loadedOfferStatus === OFFER_STATUS_ACCEPTED,
                };
                return <Step3LegalSignatures {...props} />;
            }
            default:
                return null;
        }
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId="offers"
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
                )}

                <div className="dashboard-main-content cc-contract-page">
                    <Header setIsSidebarOpen={setIsSidebarOpen} />

                    <div className="cc-page-wrapper">

                        {/* Hero header banner */}
                        <div className="cc-hero">
                            <span className="cc-hero__badge"><StarsIcon /> {t('CO_HEADER_EYEBROW')}</span>
                            <h1 className="cc-hero__title">
                                {renderHeroTitle(t(isEditMode ? 'CO_PAGE_TITLE_EDIT' : 'CO_PAGE_TITLE'))}
                            </h1>
                            <p className="cc-hero__subtitle">{t('CO_PAGE_SUBTITLE')}</p>
                        </div>

                        {(booting || isLoadingOffer) ? (
                            <CoSkeleton />
                        ) : (
                        <>

                        {/* Stepper */}
                        <div className="cc-stepper">
                            {steps.map((step, idx) => (
                                <React.Fragment key={idx}>
                                    <div
                                        className={`cc-step-item${idx === currentStep ? ' active' : ''}${idx < currentStep ? ' completed' : ''}`}
                                        onClick={() => idx < currentStep ? setCurrentStep(idx) : undefined}
                                        style={{ cursor: idx < currentStep ? 'pointer' : 'default' }}
                                    >
                                        <div className="cc-step-circle">
                                            {idx < currentStep ? <CheckIcon /> : step.icon}
                                        </div>
                                        <span className="cc-step-textwrap">
                                            <span className="cc-step-label">{step.label}</span>
                                            <span className="cc-step-subtitle">{step.subtitle}</span>
                                        </span>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className={`cc-step-connector${idx < currentStep ? ' done' : ''}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Active step */}
                        <div key={currentStep} className="cc-step-content">
                            {renderStep()}
                        </div>

                        {/* Footer */}
                        <div className="cc-footer-bar">
                            <button className="cc-btn-cancel" type="button" onClick={() => navigate(-1)}>
                                {t('CO_BTN_CANCEL')}
                            </button>
                            <div className="cc-footer-right">
                                {currentStep > 0 && (
                                    <button className="cc-btn-back" type="button" onClick={goBack}>
                                        {t('CO_BTN_BACK')}
                                    </button>
                                )}
                                {currentStep < steps.length - 1 ? (
                                    <button className="cc-btn-next" type="button" onClick={goNext}>
                                        {t('CO_BTN_NEXT')}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className="cc-btn-draft"
                                            type="button"
                                            onClick={() => handleSave('draft')}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting && submitAction === 'draft'
                                                ? <><span className="cc-spinner" /> {t('CO_BTN_SAVING_DRAFT')}</>
                                                : <>{t('CO_BTN_SAVE_DRAFT')}</>
                                            }
                                        </button>
                                        <button
                                            className="cc-btn-submit"
                                            type="button"
                                            onClick={() => handleSave('send')}
                                            disabled={isSubmitting || loadedOfferStatus === OFFER_STATUS_ACCEPTED}
                                            title={loadedOfferStatus === OFFER_STATUS_ACCEPTED ? (t('COG_ACTION_SEND_DISABLED_ACCEPTED') as string) : undefined}
                                        >
                                            {isSubmitting && submitAction === 'send'
                                                ? <><span className="cc-spinner" /> {t('CO_BTN_SENDING')}</>
                                                : <>{t('CO_BTN_SEND_OFFER')} <CheckIcon /></>
                                            }
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        </>
                        )}
                    </div>
                </div>
            </div>

            {previewOffer && createPortal(
                <div className="cc-preview-modal-overlay" role="presentation" onClick={closePreview}>
                    <div
                        className="cc-preview-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="co-preview-modal-title"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="cc-preview-modal__header">
                            <span className="cc-preview-modal__header-icon" aria-hidden="true">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <path d="M21 3L10.5 13.5M21 3l-6.5 18-4-8-8-4L21 3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <div className="cc-preview-modal__header-text">
                                <h3 id="co-preview-modal-title">{t('CO_PREVIEW_MODAL_TITLE')}</h3>
                                <p className="cc-preview-modal__subtitle">{t('CO_PREVIEW_MODAL_SUBTITLE')}</p>
                            </div>
                            <div className="cc-preview-modal__header-actions">
                                <a
                                    className="cc-preview-modal__download"
                                    href={previewOffer.pdfUrl}
                                    download="customer-offer.pdf"
                                    aria-label={t('CO_PREVIEW_DOWNLOAD') as string}
                                    data-tip={t('CO_PREVIEW_DOWNLOAD') as string}
                                >
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M12 3v11m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <span>{t('CO_PREVIEW_DOWNLOAD')}</span>
                                </a>
                                <button
                                    className="cc-preview-modal__close"
                                    type="button"
                                    onClick={closePreview}
                                    disabled={isSendingFromPreview}
                                    aria-label={t('CO_BTN_CANCEL') as string}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div
                            className="cc-preview-modal__body"
                            ref={previewScrollRef}
                            onScroll={syncPreviewEdges}
                        >
                            <div className="cc-preview-modal__pdf">
                                <Document
                                    file={previewOffer.pdfUrl}
                                    onLoadSuccess={({ numPages }: { numPages: number }) => setPreviewNumPages(numPages)}
                                    loading={<div className="cc-preview-modal__pdf-msg"><span className="cc-spinner cc-spinner--brand" /></div>}
                                    error={<div className="cc-preview-modal__pdf-msg">{t('CO_TOAST_LOAD_ERROR')}</div>}
                                >
                                    {Array.from({ length: previewNumPages }).map((_, i) => (
                                        <Page
                                            key={i}
                                            pageNumber={i + 1}
                                            width={previewPageWidth || undefined}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            className="cc-preview-modal__pdf-page"
                                        />
                                    ))}
                                </Document>
                            </div>

                            <div className="cc-preview-modal__recipient">
                                <span className="cc-preview-modal__recipient-icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                                        <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <p className="cc-preview-modal__recipient-text">
                                    {t('CO_PREVIEW_SEND_TO', {
                                        name: customers.find(c => c.customerId === selectedCustomerId)?.name ?? '',
                                        email: customers.find(c => c.customerId === selectedCustomerId)?.email ?? '',
                                    })}
                                </p>
                            </div>
                        </div>

                        {/* Floating pager — scrolls the modal content up / down */}
                        <div className="cc-preview-modal__pager" aria-hidden={previewNumPages === 0}>
                            <button
                                type="button"
                                className="cc-preview-modal__pager-btn cc-preview-modal__pager-btn--up"
                                onClick={() => swipePreview(-1)}
                                disabled={previewAtTop}
                                data-tip={t('CO_PREVIEW_SWIPE_UP') as string}
                                aria-label={t('CO_PREVIEW_SWIPE_UP') as string}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <button
                                type="button"
                                className="cc-preview-modal__pager-btn cc-preview-modal__pager-btn--down"
                                onClick={() => swipePreview(1)}
                                disabled={previewAtBottom}
                                data-tip={t('CO_PREVIEW_SWIPE_DOWN') as string}
                                aria-label={t('CO_PREVIEW_SWIPE_DOWN') as string}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>

                        <div className="cc-preview-modal__footer">
                            <button
                                className="cc-btn-back"
                                type="button"
                                onClick={closePreview}
                                disabled={isSendingFromPreview}
                            >
                                {t('CO_BTN_CANCEL')}
                            </button>
                            <button
                                className="cc-btn-submit"
                                type="button"
                                onClick={handleConfirmSendFromPreview}
                                disabled={isSendingFromPreview}
                            >
                                {isSendingFromPreview
                                    ? <><span className="cc-spinner" /> {t('CO_BTN_SENDING')}</>
                                    : <>{t('CO_BTN_CONFIRM_SEND')} <CheckIcon /></>
                                }
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CreateOfferPage;
